/**
 * Utility functions for power calculations and validation
 * 
 * AI Agent Note: This file contains the core power calculation logic.
 * The calculateTotalPowerDraw function is the heart of power management.
 */

import { Device, ValidationError } from '../types';
import { POWER_CONSTANTS, VALIDATION_MESSAGES } from '../config/constants';
import { findDeviceById, getConnectedDevices } from './deviceTree';

/**
 * Check if a device is a passthrough device (minimal self-consumption)
 * Passthrough devices transfer power but don't consume significant power themselves
 */
export function isPassthroughDevice(device: Device): boolean {
  return device.type === 'power-box' || device.type === 'usb-hub';
}

/**
 * Recursively calculate total power draw for a device and all its children
 * 
 * For passthrough devices (power-box, usb-hub), only counts children's power draw
 * plus a small percentage (1%) of the children's draw for the device itself.
 * 
 * USB-only devices are NOT included in power calculations because their power
 * (from USB 5V rail) is already included in the parent's powerRequirement.
 * 
 * This is a critical function used in multiple places:
 * - DeviceContext for validation
 * - PowerVisualization for display
 * 
 * @param deviceId - ID of the device to calculate power for
 * @param devices - Array of all devices
 * @returns Total power draw in Watts (device + all children recursively)
 */
export function calculateTotalPowerDraw(deviceId: string, devices: Device[]): number {
  const device = findDeviceById(deviceId, devices);
  if (!device) return 0;
  
  // Only count power-connected children (exclude USB-only devices)
  // USB devices draw power from the 5V USB rail, already budgeted in parent's powerRequirement
  const powerConnectedChildren = devices.filter(d => {
    // Explicit power connection
    if (d.powerConnection?.sourceDeviceId === deviceId) return true;
    // Legacy connection without explicit routing (assume power connection)
    if (d.connectedTo === deviceId && !d.powerConnection && !d.usbConnection) return true;
    return false;
  });
  
  const childrenPower = powerConnectedChildren.reduce((sum, child) => {
    return sum + calculateTotalPowerDraw(child.id, devices);
  }, 0);
  
  // For passthrough devices, only count children's power + 1% overhead
  if (isPassthroughDevice(device)) {
    return childrenPower + (childrenPower * 0.01);
  }
  
  // For regular devices, count device power + all children
  return device.powerRequirement + childrenPower;
}

/**
 * Format power value for display with consistent decimal places
 * @param watts - Power value in Watts
 * @returns Formatted string
 */
export function formatPower(watts: number): string {
  return watts.toFixed(POWER_CONSTANTS.POWER_DECIMALS);
}

/**
 * Calculate current (amps) from power (watts) and voltage
 * @param watts - Power in Watts
 * @param voltage - Voltage in Volts
 * @returns Current in Amps
 */
export function calculateAmps(watts: number, voltage: number): number {
  if (voltage === 0) return 0;
  return watts / voltage;
}

/**
 * Format amps for display with consistent decimal places
 * @param amps - Current in Amps
 * @returns Formatted string
 */
export function formatAmps(amps: number): string {
  return amps.toFixed(2);
}

/**
 * Get the primary voltage for a device (from first power input or output)
 * @param device - Device to get voltage from
 * @returns Voltage in Volts, defaults to 12V if not specified
 */
export function getDeviceVoltage(device: Device): number {
  // Check power input first
  if (device.powerInput?.voltage) {
    return device.powerInput.voltage;
  }
  // Check power outputs
  if (device.powerOutputs && device.powerOutputs.length > 0) {
    return device.powerOutputs[0].voltage;
  }
  // Default to 12V for common astro gear
  return 12;
}

/**
 * Check if a device's passthrough power is exceeded by connected devices
 * @param device - Device to check
 * @param devices - Array of all devices
 * @returns ValidationError if exceeded, null otherwise
 */
export function checkPassthroughExceeded(device: Device, devices: Device[]): ValidationError | null {
  if (device.passthroughPower <= 0) return null;
  
  // Only count devices with POWER connections, not USB-only connections
  const powerConnectedDevices = devices.filter(d => {
    // Check explicit power connection
    if (d.powerConnection?.sourceDeviceId === device.id) return true;
    // Check legacy connectedTo (but only if no explicit connections exist)
    if (d.connectedTo === device.id && !d.powerConnection && !d.usbConnection) return true;
    return false;
  });
  
  const totalChildPower = powerConnectedDevices.reduce((sum, d) => {
    return sum + calculateTotalPowerDraw(d.id, devices);
  }, 0);
  
  if (totalChildPower > device.passthroughPower) {
    return {
      deviceId: device.id,
      deviceName: device.name,
      type: 'power-exceeded',
      message: VALIDATION_MESSAGES.POWER_CHAIN_EXCEEDED(
        device.name,
        device.passthroughPower,
        totalChildPower
      ),
      severity: 'error'
    };
  }
  
  return null;
}

/**
 * Check if a device's total current limit is exceeded by connected devices
 * @param device - Device to check
 * @param devices - Array of all devices
 * @returns ValidationError if exceeded, null otherwise
 */
export function checkTotalCurrentExceeded(device: Device, devices: Device[]): ValidationError | null {
  // Only check devices with a total current limit
  if (!device.maxTotalCurrent || device.maxTotalCurrent <= 0) return null;
  
  // Only count devices that draw power from this device (not USB-only devices)
  const powerConnectedDevices = devices.filter(d => {
    // Explicit power connection
    if (d.powerConnection?.sourceDeviceId === device.id) return true;
    // Legacy connection without explicit routing (assume power connection)
    if (d.connectedTo === device.id && !d.powerConnection && !d.usbConnection) return true;
    return false;
  });
  
  if (powerConnectedDevices.length === 0) return null;
  
  const voltage = getDeviceVoltage(device);
  
  // Calculate total current draw from all power-connected devices
  const totalCurrentDraw = powerConnectedDevices.reduce((sum, child) => {
    const childPower = calculateTotalPowerDraw(child.id, devices);
    const childCurrent = calculateAmps(childPower, voltage);
    return sum + childCurrent;
  }, 0);
  
  if (totalCurrentDraw > device.maxTotalCurrent) {
    const exceeded = totalCurrentDraw - device.maxTotalCurrent;
    return {
      deviceId: device.id,
      deviceName: device.name,
      type: 'power-exceeded',
      message: `${device.name} total current limit (${device.maxTotalCurrent.toFixed(2)}A) exceeded by ${exceeded.toFixed(2)}A (total draw: ${totalCurrentDraw.toFixed(2)}A @ ${voltage}V)`,
      severity: 'error'
    };
  }
  
  return null;
}

/**
 * Check if a power source's capacity is exceeded
 * @param device - Power source device to check
 * @param devices - Array of all devices
 * @returns ValidationError if exceeded, null otherwise
 */
export function checkPowerSourceExceeded(device: Device, devices: Device[]): ValidationError | null {
  if (device.type !== 'power-source') return null;
  
  const totalPowerDraw = calculateTotalPowerDraw(device.id, devices) - device.powerRequirement;
  
  if (totalPowerDraw > device.passthroughPower) {
    const exceeded = totalPowerDraw - device.passthroughPower;
    return {
      deviceId: device.id,
      deviceName: device.name,
      type: 'power-exceeded',
      message: VALIDATION_MESSAGES.POWER_EXCEEDED(device.name, device.passthroughPower, exceeded),
      severity: 'error'
    };
  }
  
  return null;
}

/**
 * Check if a device is missing a power source connection
 * @param device - Device to check
 * @returns ValidationError if missing power source, null otherwise
 */
export function checkMissingPowerSource(device: Device): ValidationError | null {
  // Power sources don't need a power connection
  if (device.type === 'power-source') {
    return null;
  }
  
  // If device has no power connection AND no powered USB connection, it needs power
  const hasNoPowerConnection = !device.connectedTo && !device.powerConnection;
  const hasNoPoweredUSB = !device.usbConnection;
  
  // If no power and no powered USB, flag as warning
  if (hasNoPowerConnection && hasNoPoweredUSB) {
    return {
      deviceId: device.id,
      deviceName: device.name,
      type: 'no-power-source',
      message: VALIDATION_MESSAGES.NO_POWER_SOURCE(device.name),
      severity: 'warning'
    };
  }
  
  return null;
}
